import { t } from '@transifex/native';

import * as flashActions from 'pkg/actions/flashes';
import { errorCode, RefusalCode } from 'pkg/actions/form_event_group_waitlist';

import * as endpoints from 'pkg/api/endpoints/auto';
import * as models from 'pkg/api/models';
import type { FormSubmissionInviteStatus } from 'pkg/api/models/form_submission';
import { hasAllowedAction } from 'pkg/api/models/guards';
import * as sdk from 'pkg/core/sdk';

export interface CreateInvitePayload {
	userId: number;
	inviteStatus: FormSubmissionInviteStatus;
	targetGroupId?: number;
}

export interface CreateInvitesResponse {
	formSubmissionInvites: models.formSubmission.FormSubmission[];
}

export interface TeamInvitationAssignment {
	submissionId: number;
	groupId: number;
	groupName: string;
	userId: number;
	user?: models.user.User;
	inviteStatus: FormSubmissionInviteStatus;
	canResend?: boolean;
}

export async function createInvites(
	organizationId: number,
	formId: number,
	invites: CreateInvitePayload[],
	emailParams?: UpdateInvitesEmailParams
): Promise<[Response, CreateInvitesResponse | null]> {
	const request = await sdk.post(
		endpoints.Forms.CreateInvites(organizationId, formId),
		{ allowed_actions: models.Action.FormSubmissionTeamInvitationResend },
		{ invites, ...emailParams }
	);

	if (!request.ok) {
		return [request, null];
	}

	const response: CreateInvitesResponse = await request.json();
	return [request, response];
}

export async function addPlayerToGroup(
	organizationId: number,
	formId: number,
	groupId: number,
	groupName: string,
	user: models.user.User,
	inviteStatus: FormSubmissionInviteStatus
): Promise<TeamInvitationAssignment | null> {
	const [request, response] = await createInvites(organizationId, formId, [
		{
			userId: user.id,
			inviteStatus,
			targetGroupId: groupId,
		},
	]);

	if (request.ok && response?.formSubmissionInvites?.[0]) {
		const submission = response.formSubmissionInvites[0];
		return {
			submissionId: submission.id,
			groupId,
			groupName,
			userId: submission.submittedForUserId ?? user.id,
			user,
			inviteStatus: submission.inviteStatus!,
			canResend: hasAllowedAction(
				submission,
				models.Action.FormSubmissionTeamInvitationResend
			),
		};
	}

	flashActions.show({ title: t('Failed to assign player') });
	return null;
}

export async function addPlayersToGroup(
	organizationId: number,
	formId: number,
	groupId: number,
	groupName: string,
	users: models.user.User[],
	inviteStatus: FormSubmissionInviteStatus
): Promise<TeamInvitationAssignment[]> {
	if (users.length === 0) return [];

	const [request, response] = await createInvites(
		organizationId,
		formId,
		users.map((user) => ({
			userId: user.id,
			inviteStatus,
			targetGroupId: groupId,
		}))
	);

	if (request.ok && response?.formSubmissionInvites) {
		return response.formSubmissionInvites.map((submission) => {
			const originalUser = users.find(
				(u) => u.id === submission.submittedForUserId
			);
			return {
				submissionId: submission.id,
				groupId,
				groupName,
				userId: submission.submittedForUserId!,
				user: originalUser ?? submission.submittedForUser,
				inviteStatus: submission.inviteStatus!,
				canResend: hasAllowedAction(
					submission,
					models.Action.FormSubmissionTeamInvitationResend
				),
			};
		});
	}

	flashActions.show({ title: t('Failed to assign players') });
	return [];
}

export interface UpdateInvitePayload {
	id: number;
	inviteStatus: FormSubmissionInviteStatus;
	// Omitted for group-less invitations so the backend keeps a null target group.
	targetGroupId?: number;
}

export interface UpdateInvitesResponse {
	formSubmissionInvites: models.formSubmission.FormSubmission[];
}

export interface UpdateInvitesEmailParams {
	emailSubject?: string;
	emailContent?: string;
}

export async function updateInvites(
	organizationId: number,
	formId: number,
	invites: UpdateInvitePayload[],
	emailParams?: UpdateInvitesEmailParams
): Promise<[Response, UpdateInvitesResponse | null]> {
	const request = await sdk.patch(
		endpoints.Forms.UpdateInvites(organizationId, formId),
		{ allowed_actions: models.Action.FormSubmissionTeamInvitationResend },
		{ invites, ...emailParams }
	);

	if (!request.ok) {
		return [request, null];
	}

	const response: UpdateInvitesResponse = await request.json();
	return [request, response];
}

export async function removePlayerFromGroup(
	organizationId: number,
	formId: number,
	submissionId: number
): Promise<boolean> {
	const request = await sdk.destroy(
		endpoints.Forms.DeleteInvites(organizationId, formId),
		{},
		{ inviteIds: [submissionId] }
	);

	if (!request.ok) {
		flashActions.show({ title: t('Failed to remove player') });
	}

	return request.ok;
}

export async function revokeInvitation(
	organizationId: number,
	formId: number,
	submissionId: number,
	groupId: number
): Promise<boolean> {
	const [request] = await updateInvites(organizationId, formId, [
		{
			id: submissionId,
			inviteStatus: models.formSubmission.FormSubmissionInviteStatus.Revoked,
			// A group-less invitation (groupId 0) must keep a null target group, so omit it.
			targetGroupId: groupId || undefined,
		},
	]);

	if (!request.ok) {
		flashActions.show({ title: t('Failed to revoke invitation') });
	}

	return request.ok;
}

export async function rejectInvite(
	organizationId: number,
	formSubmissionId: number
): Promise<boolean> {
	const request = await sdk.post(
		endpoints.FormSubmissions.RejectInvite(organizationId, formSubmissionId)
	);

	if (!request.ok) {
		flashActions.show({ title: t('Failed to decline invitation') });
	}

	return request.ok;
}

export async function resendInvitation(
	url: string,
	inviteId: number,
	targetGroupId?: number,
	emailParams?: UpdateInvitesEmailParams
): Promise<boolean> {
	const request = await sdk.patch(
		url,
		{},
		{
			invites: [
				{
					id: inviteId,
					inviteStatus:
						models.formSubmission.FormSubmissionInviteStatus.Invited,
					...(targetGroupId ? { targetGroupId } : {}),
				},
			],
			...emailParams,
		}
	);

	if (!request.ok) {
		let message: string | undefined;

		if ((await errorCode(request)) === RefusalCode.ResendWaitlisted) {
			message = t(
				"They're on the waitlist for a program on this form. Invite them from that program's Waitlist tab."
			);
		}

		flashActions.show({ title: t('Failed to resend invitation'), message });
	}

	return request.ok;
}

export async function invitePlayers(
	organizationId: number,
	formId: number,
	mappings: TeamInvitationAssignment[],
	emailParams?: UpdateInvitesEmailParams
): Promise<TeamInvitationAssignment[]> {
	if (mappings.length === 0) return [];

	const invitePayloads = mappings.map((mapping) => ({
		id: mapping.submissionId,
		inviteStatus: models.formSubmission.FormSubmissionInviteStatus.Invited,
		targetGroupId: mapping.groupId,
	}));

	const [request, response] = await updateInvites(
		organizationId,
		formId,
		invitePayloads,
		emailParams
	);

	if (request.ok && response?.formSubmissionInvites) {
		return response.formSubmissionInvites.map((submission) => {
			const originalMapping = mappings.find(
				(m) => m.submissionId === submission.id
			);
			return {
				submissionId: submission.id,
				groupId: originalMapping?.groupId ?? 0,
				groupName: originalMapping?.groupName ?? '',
				userId: submission.submittedForUserId!,
				user: originalMapping?.user ?? submission.submittedForUser,
				inviteStatus: submission.inviteStatus!,
				canResend: hasAllowedAction(
					submission,
					models.Action.FormSubmissionTeamInvitationResend
				),
			};
		});
	}

	flashActions.show({ title: t('Failed to send invitations') });
	return [];
}
